import { OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Agenda } from 'app/model/agenda';
import { Agende } from 'app/model/agende';
import { IAppuntamento } from 'app/model/appuntamento';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { delay, filter } from 'rxjs/operators';
import { HashMap, Pair, TreeMultiSet, make_pair } from 'tstl';
import { isObject } from 'util';
import { OAuth2Service, WebSocketReadyState } from '../angular-rest';

// TODO: Questa si potrebbe astrarre ulteriormente...
export abstract class PartitionedEventListenerBase<K> implements OnDestroy {
	// Le partizioni sono per chiave e le partizioni sono ordinate internamente
	protected partitions: HashMap<K, TreeMultiSet<IAppuntamento>>;
	
	// Id => (partition, iterator[])
	private idTable: HashMap<number, Pair<K, TreeMultiSet.Iterator<IAppuntamento>>[]>;
	private subscriptions: Subscription;
	private _agenda: Agenda;
	protected finalizers = new Subscription();

	constructor(
		route: ActivatedRoute, 
		agende: Agende, 
		router: Router, 
		protected auther: OAuth2Service,
		protected eventFilter = (event: IAppuntamento) => true
	) {
		this.finalizers = Observable.combineLatest(route.parent.params, agende)
            .subscribe(([params, _agende]) => {
                const agenda = _agende.find(a =>
                    a.codice_fiscale === params.codice_fiscale &&
                    a.nome === params.agenda
                );

                if (!agenda) {
                    router.navigateByUrl('/agende');
                } else {
                    this.agenda = agenda;
                }
			});
    }

	protected upsertEvent: (appuntamento: IAppuntamento) => void;
	protected deleteEvent: (appuntamento: number) => void;
	protected deleteGroup: (appuntamento: number) => void;
	protected abstract getPartitionKeys(appuntamento: IAppuntamento): K[];

    set agenda(agenda: Agenda) {
		this._agenda = agenda;
		this.setup(agenda);
	}

	get agenda(): Agenda {
		return this._agenda
	}
	
	public getPartition(key: K): TreeMultiSet<IAppuntamento> {
		if(this.partitions) {
			const it = this.partitions.find(key);
			if(it.equals(this.partitions.end())) {
				return null;
			} else {
				return it.second;
			}
		} else {
			return null;
		}
	}

    protected setup(
		agenda: Agenda, 
		start: moment.Moment = moment().startOf('day'), 
		end: moment.Moment = moment().startOf('day').add(1, 'day')
	) {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }

        this.finalizers.add(this.subscriptions = new Subscription());

		/* (a, b) => b.start > a.start */
        this.partitions = new HashMap<K, TreeMultiSet<IAppuntamento>>();
		this.idTable = new HashMap<number, Pair<K, TreeMultiSet.Iterator<IAppuntamento>>[]>();


        let sock = agenda.getEventSocket(start, end);
		this.upsertEvent = (event: IAppuntamento) => sock.upsert(event);
		this.deleteEvent = (event: number) => sock.delete(event);
		this.deleteGroup = (event: number) => sock.delete_group(event);


        this.subscriptions.add(
            // TODO sock.conflicts,
            sock.updates.pipe(
				filter(this.eventFilter)
			).subscribe({
				error: error => {
					if (isObject(error) && 'errno' in error) {
						switch (error.errno) {
							case 401:
							this.auther.refresh()
							break;
						}
					}
				},
				next: event => {
					const oldIt = this.idTable.find(+event.id);
					const keys = this.getPartitionKeys(event);
					
					const ereaseOld = () => {
						for (const ref of oldIt.second) {
							this.partitions.get(ref.first).erase(ref.second);
						}
					}

					// Se non c'è partizione possiamo uscire dopo aver eliminato
					// l'eventuale riferimento al vecchio
					if (keys == null || !keys.length) {
						if(!oldIt.equals(this.idTable.end())) {
							// Lo cancello!
							ereaseOld();
							this.idTable.erase(oldIt);
						}
						
						return;
					}

					// Se è nell'intervallo...
					// [a, b] overlaps with [x, y] iff b > x and a < y
					if (event.allDay || (moment.utc(event.start).isBefore(end) && moment.utc(event.end).isAfter(start))) {
						// Inserisco l'evento nelle partizioni
						const refs = keys.map(key => {
							let partition = this.partitions.find(key);
							// Se non esiste la creo
							if(partition.equals(this.partitions.end())) {
								partition = this.partitions.insert(make_pair(
									key,
									new TreeMultiSet<IAppuntamento>((a, b) => b.start > a.start)
								)).first;
							}

							return make_pair(key, partition.second.insert(event));
						});

						// Ora controllo se devo cancellare la vecchia versione
						if (oldIt.equals(this.idTable.end())) {
							// Non c'era! Allora devo solo aggiungerlo!
							this.idTable.insert(make_pair(+event.id, refs));
						} else {
							// Devo eliminare quello vecchio
							ereaseOld();
							this.idTable.set(+event.id, refs);
						}
					} else {
						// Se invece l'evento è fuori dall'intervallo di riferimento
						// ed esiste (era parte della visualizzazione)
						if (!oldIt.equals(this.idTable.end())) {
							// Lo cancello!
							ereaseOld();
							this.idTable.erase(oldIt);
						}
					}
				}
			}));

        this.subscriptions.add(
            sock.deletes.subscribe(event => {
                const oldIt = this.idTable.find(event.delete);
                if (!oldIt.equals(this.idTable.end())) {
					for (const ref of oldIt.second) {
						this.partitions.get(ref.first).erase(ref.second);
					}
					this.idTable.erase(oldIt);
                }
            }),
        );

        let innerSubscription = new Subscription();

        this.subscriptions.add(sock.status.subscribe(s => {
            if (s === WebSocketReadyState.CLOSED) {
                innerSubscription.unsubscribe();
                this.subscriptions.add(
                    innerSubscription = Observable.of(agenda).pipe(
                        delay(1000)
					).subscribe(a => this.agenda = a)
                );
            }
        }));
        // TODO: sock.postback
    }

    ngOnDestroy() {
        this.finalizers.unsubscribe();
    }

}


// WEBPACK FOOTER //
// ./src/app/calendar/event_listener_base.ts